// src/pages/ConfirmationPage.jsx

import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import PageLoader from 'b2b/components/PageLoader';
import { getBookingsBaseUrl, getAuthHeaders } from '../../utils/bookingsBase';

export default function ConfirmationPage() {
  const { bookingId } = useParams();
  const history = useHistory();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchBooking() {
      try {
        setLoading(true);
        setSaving(true);

        const base = getBookingsBaseUrl();
        const res = await axios.get(`${base}/${bookingId}`, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });

        if (!cancelled) {
          setBooking(res.data);
        }
      } catch (err) {
        console.error('FETCH_BOOKING_FAILED', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSaving(false);
        }
      }
    }

    fetchBooking();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  if (loading || !booking) {
    return (
      <div className="container" style={{ marginTop: '7rem' }}>
        <PageLoader show label="Chargement de la confirmation…" />
      </div>
    );
  }

  // dates (on reçoit "YYYY-MM-DD")
  const startLabel = booking.stayStartDate
    ? new Date(booking.stayStartDate).toLocaleDateString('fr-FR')
    : null;
  const endLabel = booking.stayEndDate
    ? new Date(booking.stayEndDate).toLocaleDateString('fr-FR')
    : null;

  const durationLabel = `${booking.durationDays || 0} jours / ${
    booking.durationNights || 0
  } nuits`;

  // prix
  const fmtPrice = (v) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(v || 0));

  const paymentMode = booking.paymentMode || 'card';
  const paymentCardOption = booking.paymentCardOption || 'full';

  const getPaymentLabel = () => {
    if (paymentMode === 'account') return 'Paiement en compte';
    if (paymentMode === 'card' && paymentCardOption === 'deposit40')
      return 'Carte bancaire (acompte 40%)';
    return 'Carte bancaire (paiement total)';
  };

  const computePaidAndRemaining = () => {
    const total = Number(booking.finalTotalPrice || booking.totalPrice || 0);

    // Si tu stockes des centimes, on préfère ça (plus fiable)
    const centsPaid =
      Number.isFinite(Number(booking.paymentAmountCents)) &&
      Number(booking.paymentAmountCents) > 0
        ? Number(booking.paymentAmountCents)
        : null;

    if (centsPaid != null) {
      const paid = Number((centsPaid / 100).toFixed(2));
      const remaining = Number((total - paid).toFixed(2));
      return { paid: Math.max(paid, 0), remaining: Math.max(remaining, 0), total };
    }

    // Sinon calcul métier
    if (paymentMode === 'card') {
      if (paymentCardOption === 'deposit40') {
        const paid = Number((total * 0.3).toFixed(2));
        const remaining = Number((total - paid).toFixed(2));
        return { paid, remaining, total };
      }
      return { paid: total, remaining: 0, total };
    }

    // account
    return { paid: 0, remaining: total, total };
  };

  const { paid: paidAmount, remaining: remainingAmount } = computePaidAndRemaining();
  const paymentLabel = getPaymentLabel();

  const totalPrice = Number(booking.totalPrice || 0);
  const finalTotalPrice = Number(booking.finalTotalPrice || 0);
  const discountPercent = Number(booking.discountValue || 0);
  const discountAmount = Math.max(totalPrice - finalTotalPrice, 0);

  const isB2B = booking?.userId != null;

  return (
    <div style={{ margin: '50px' }}>
      <PageLoader show={saving} label="Chargement de la confirmation…" />

      <div className="container mt-4">
        <h1 className="mb-3 text-success">RÉSERVATION CONFIRMÉE 🎉</h1>

        <p className="lead">Merci, votre réservation a été réglée avec succès.</p>

        {/* phrase sur l'email */}
        <p className="mb-4">
          Merci d’accéder à votre boîte mail pour consulter le récapitulatif détaillé de votre
          réservation.
        </p>

        <div className="card my-4">
          <div className="card-header fw-semibold">
            {' '}
            <strong>N° de dossier :</strong>{' '}
            {booking.pgiBookingCode ? booking.pgiBookingCode : '—'}
          </div>

          <div className="card-body">
            {/* image + titre */}
            <div className="d-flex flex-wrap mb-3">
              {booking.productImage && (
                <div style={{ maxWidth: 400 }} className="me-3 mb-3">
                  <img
                    src={booking.productImage}
                    alt={booking.productName}
                    className="img-fluid rounded"
                    style={{ objectFit: 'cover', width: '100%' }}
                  />
                </div>
              )}

              <div
                className="flex-grow-1"
                style={{ alignContent: 'center', paddingLeft: '20px' }}
              >
                <h4 className="mb-1" style={{ color: '#000064' }}>
                  {booking.productName}
                </h4>
                {booking.destination?.city && (
                  <p className="mb-1">
                    <strong> Destination :</strong> {booking.destination.city}
                  </p>
                )}
                {booking.fromLabel && (
                  <p className="mb-1">
                    <strong>Ville de départ :</strong> {booking.fromLabel}
                  </p>
                )}
                {startLabel && endLabel && (
                  <p className="mb-1">
                    <strong>Dates du séjour :</strong> {startLabel} → {endLabel}
                  </p>
                )}
                <p className="mb-0">
                  <strong>Durée :</strong> {durationLabel}
                </p>
              </div>
            </div>

            <hr />

            {/* bloc paiement */}
            <div>
              <h5 className="mb-2" style={{ color: '#000064' }}>
                Votre règlement
              </h5>

              <p className="mb-1">
                <strong>Mode de paiement :</strong> {paymentLabel}
              </p>

              <p className="mb-1">
                <strong>Montant réglé :</strong> {fmtPrice(paidAmount)}
              </p>

              {remainingAmount > 0 && (
                <p className="mb-1 text-danger">
                  <strong>Solde restant :</strong> {fmtPrice(remainingAmount)}
                </p>
              )}
            </div>
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() =>
            history.push(isB2B ? '/b2b/mes-reservations' : '/checking-reservation-details')
          }
        >
          Voir ma réservation
        </button>
      </div>
    </div>
  );
}
